import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import fallbackTranslations from '../data/ui-texts.json';

const CACHE_KEY = 'ui_translations_cache';
const VERSION_KEY = 'ui_translations_version';
const REVERSE_INDEX_KEY = 'ui_translations_reverse_index';

interface UITranslationsData {
  [key: string]: any;
}

// Type pour l'index inversé: texte français → objet de traductions
interface ReverseIndex {
  [frenchText: string]: {
    [lang: string]: string;
  };
}

/**
 * Hook pour charger les traductions UI depuis Supabase avec cache et versioning
 *
 * Stratégie :
 * 1. Charge depuis cache local (instantané)
 * 2. Vérifie la version distante (requête légère)
 * 3. Si version différente → fetch complet + update cache
 * 4. Fallback sur ui-texts.json bundled si erreur
 */
export const useUITranslations = () => {
  const [translations, setTranslations] = useState<UITranslationsData>(fallbackTranslations);
  const [reverseIndex, setReverseIndex] = useState<ReverseIndex>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Convertit un array flat de Supabase en objet imbriqué
   * Input:  [{key: "navigation.back", translations: {...}}]
   * Output: {navigation: {back: {...}}}
   */
  const formatToNestedObject = (data: any[]): UITranslationsData => {
    const result: UITranslationsData = {};

    data.forEach(({ key, translations }) => {
      const keys = key.split('.');
      let current = result;

      keys.forEach((k: string, i: number) => {
        if (i === keys.length - 1) {
          // Dernière clé : assigner les traductions
          current[k] = translations;
        } else {
          // Clé intermédiaire : créer l'objet si n'existe pas
          current[k] = current[k] || {};
          current = current[k];
        }
      });
    });

    return result;
  };

  /**
   * Construit un index inversé: texte français → traductions
   * Permet de faire t("Paramètres") et obtenir la traduction
   */
  const buildReverseIndex = (data: any[]): ReverseIndex => {
    const index: ReverseIndex = {};

    data.forEach(({ translations }) => {
      if (translations && typeof translations === 'object' && translations.fr) {
        // Normaliser la clé française (trim, lowercase pour matching)
        const frKey = String(translations.fr).trim();
        index[frKey] = translations;
      }
    });

    return index;
  };

  /**
   * Charge les traductions avec stratégie de cache
   */
  const loadTranslations = useCallback(async () => {
    try {
      // 1️⃣ Charger depuis cache local (instantané)
      const cached = localStorage.getItem(CACHE_KEY);
      const cachedReverseIndex = localStorage.getItem(REVERSE_INDEX_KEY);
      const localVersion = localStorage.getItem(VERSION_KEY);

      if (cached) {
        const parsedCache = JSON.parse(cached);
        console.log('📦 Traductions UI chargées depuis cache (version', localVersion, ')');
        setTranslations(parsedCache);

        // Charger aussi l'index inversé si disponible
        if (cachedReverseIndex) {
          setReverseIndex(JSON.parse(cachedReverseIndex));
        }
        setLoading(false);
      } else {
        // Première installation : fallback bundled
        console.log('📦 Première installation, fallback sur ui-texts.json bundled');
        setTranslations(fallbackTranslations);
        setLoading(false);
      }

      // 2️⃣ Vérifier la version distante (requête légère : 1 ligne)
      const { data: metadata, error: metadataError } = await supabase
        .from('ui_translations_metadata')
        .select('version')
        .single();

      if (metadataError) {
        console.warn('⚠️ Erreur lors de la vérification de version:', metadataError);
        // Garder le cache/fallback, pas grave
        return;
      }

      const remoteVersion = metadata?.version;

      console.log('🔍 Version locale:', localVersion, '| Version distante:', remoteVersion);

      // 3️⃣ Si nouvelle version dispo → fetch complet
      if (!localVersion || Number(localVersion) < remoteVersion) {
        console.log('🔄 Nouvelle version détectée, téléchargement...');

        const { data, error: fetchError } = await supabase
          .from('ui_translations')
          .select('key, translations');

        if (fetchError) {
          console.error('❌ Erreur lors du fetch des traductions:', fetchError);
          // Garder le cache/fallback
          return;
        }

        if (data && data.length > 0) {
          // Convertir flat → nested
          const formatted = formatToNestedObject(data);

          // Construire l'index inversé pour la fonction t()
          const newReverseIndex = buildReverseIndex(data);

          // Sauvegarder en cache
          localStorage.setItem(CACHE_KEY, JSON.stringify(formatted));
          localStorage.setItem(REVERSE_INDEX_KEY, JSON.stringify(newReverseIndex));
          localStorage.setItem(VERSION_KEY, String(remoteVersion));

          setTranslations(formatted);
          setReverseIndex(newReverseIndex);
          console.log(`✅ Traductions UI mises à jour (version ${remoteVersion}, ${data.length} entrées, ${Object.keys(newReverseIndex).length} clés indexées)`);
        }
      } else {
        console.log('✅ Cache à jour, pas de téléchargement nécessaire');
      }

    } catch (err) {
      console.warn('⚠️ Erreur lors du chargement des traductions, fallback bundled:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setTranslations(fallbackTranslations);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger au mount
  useEffect(() => {
    loadTranslations();
  }, [loadTranslations]);

  return {
    translations,
    reverseIndex,
    loading,
    error,
    refetch: loadTranslations
  };
};

export default useUITranslations;
export type { ReverseIndex };
